import React from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Box, ButtonBase, Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { IncognitoIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Inline } from "components/layout/reusableLayouts";
import { useImpersonation } from "../../contexts/ImpersonationContext";
import { END_IMPERSONATION_SESSION } from "../../graphql/impersonation";

const ImpersonationBanner: React.FC = () => {
  const { session, endSession } = useImpersonation();
  const apolloClient = useApolloClient();
  const { t } = useTranslation();
  const [endImpersonation, { loading }] = useMutation(
    END_IMPERSONATION_SESSION,
  );

  if (!session) return null;

  const handleExit = async () => {
    try {
      await endImpersonation({ variables: { sessionId: session.sessionId } });
    } catch (err) {
      console.error("Failed to end impersonation session", err);
    } finally {
      endSession();
      await apolloClient.resetStore();
      window.location.assign("/");
    }
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        width: "100%",
        backgroundColor: "var(--color-bg-brand)",
        color: "var(--color-text-on-brand)",
        boxShadow: "var(--shadow-md)",
      }}
    >
      <Inline
        justify="center"
        gap="var(--space-3) var(--space-5)"
        sx={{ padding: "var(--space-4) var(--space-7)" }}
      >
        <HugeiconsIcon icon={IncognitoIcon} size={24} />
        <Typography variant="body1" component="span" color="inherit">
          {t("You are impersonating")}{" "}
          <Box component="strong">{session.target.email}</Box>
          {session.target.companyName ? (
            <>
              {" · "}
              <Box component="strong">{session.target.companyName}</Box>
            </>
          ) : null}
        </Typography>
        <ButtonBase
          onClick={handleExit}
          disabled={loading}
          sx={{
            flexShrink: 0,
            padding: "var(--space-3) var(--space-5)",
            borderRadius: "var(--radius-full)",
            backgroundColor: "var(--color-text-on-brand)",
            color: "var(--color-bg-brand)",
            "&:hover": { opacity: 0.9 },
            "&.Mui-disabled": { opacity: 0.5 },
          }}
        >
          <Typography
            variant="body2"
            component="span"
            color="inherit"
            sx={{ fontWeight: 600 }}
          >
            {loading ? t("Stopping") : t("Stop impersonating")}
          </Typography>
        </ButtonBase>
      </Inline>
    </Box>
  );
};

export default ImpersonationBanner;
